/** @jsx jsx */
import {jsx} from '@emotion/core'
import {observer} from 'mobx-react'
import React, {Fragment} from 'react'
import dayjs from 'dayjs'
import 'dayjs/locale/ko'
import {FEED_SUPPLIER} from '@v2/constants/common'
import {
  LS_REGULAR_ODDS_HANDICAP,
  LS_REGULAR_ODDS_UNDEROVER,
  UNIFIED_REGULAR_ODDS_HANDICAP,
  UNIFIED_REGULAR_ODDS_UNDEROVER,
} from '@game-type/game-market-types'
import {BET_HIT_STATE, MATCH_STATUS} from 'adv-protocol/src/GameCommon'
import {isCanceledMatchStatus, isOngoingMatchStatus} from '@utils/utils'
import { BASIC_TEAM_EMBLEM_MAP } from '@shared/EmblemGroup'

dayjs.locale('ko')

export interface IMatchInfo {
  dateOfMatch: Date
  sportId: number
  homeTeamName: string
  homeTeamEmblem: string
  homeTeamScore: number
  awayTeamName: string
  awayTeamEmblem: string
  awayTeamScore: number
  feedSupplier: string
  marketType: string
  outcomeRate: string
  hitState: number
  matchStatus: MATCH_STATUS
}

export interface MatchInfoProps {
  matchInfo: IMatchInfo
  large?: boolean
  small?: boolean
  vertical?: boolean
  horizon?: boolean
}

export const CompanyPickMatchInfo: React.FC<MatchInfoProps> = observer((props) => {
  const {matchInfo, small = true, vertical = false} = props
  const {
    dateOfMatch,
    sportId,
    homeTeamName,
    homeTeamEmblem,
    homeTeamScore,
    awayTeamName,
    awayTeamEmblem,
    awayTeamScore,
    feedSupplier,
    marketType,
    outcomeRate,
    hitState,
    matchStatus,
  } = matchInfo

  const date = dayjs(dateOfMatch).format('MM/DD(ddd)')
  const time = dayjs(dateOfMatch).format('HH:mm')

  let contents: React.ReactElement

  const handicapElement = <span className="market-type">H</span>
  const underOverElement = <span className="market-type">U/O</span>

  let marketTypeElement
  if (feedSupplier === FEED_SUPPLIER.BET_RADAR) {
    if (UNIFIED_REGULAR_ODDS_HANDICAP.indexOf(marketType) >= 0) {
      marketTypeElement = handicapElement
    } else if (UNIFIED_REGULAR_ODDS_UNDEROVER.indexOf(marketType) >= 0) {
      marketTypeElement = underOverElement
    }
  } else if (feedSupplier === FEED_SUPPLIER.LSPORTS) {
    if (LS_REGULAR_ODDS_HANDICAP.indexOf(marketType) >= 0) {
      marketTypeElement = handicapElement
    } else if (LS_REGULAR_ODDS_UNDEROVER.indexOf(marketType) >= 0) {
      marketTypeElement = underOverElement
    }
  }

  let isLive = false
  let isClosed = false
  let isCanceled = false
  let hitStateElement
  switch (hitState) {
    case BET_HIT_STATE.SUCCESS:
      isClosed = true
      hitStateElement = <span className="success hit-state"/>
      break
    case BET_HIT_STATE.FAIL:
      isClosed = true
      hitStateElement = <span className="fail hit-state"/>
      break
    case BET_HIT_STATE.CANCEL:
      isClosed = true
      hitStateElement = <span className="cancel hit-state"/>
      break
    default:
      break
  }

  const homeScoreClassName = homeTeamScore > awayTeamScore ? 'win' : ''
  const awayScoreClassName = homeTeamScore < awayTeamScore ? 'win' : ''

  if (isCanceledMatchStatus(matchStatus)) {
    isLive = false
    isClosed = false
    isCanceled = true
  } else if (isOngoingMatchStatus(matchStatus)) {
    isLive = true
    isClosed = false
    isCanceled = false
  }

  let pickStatus
  if (isLive) {
    pickStatus = <span className="live-tag">진행중</span>
  } else {
    pickStatus = (
      <Fragment>
        <div className="market-info">
          {/* 핸디캡, 언더오버만 표시 */}
          {marketTypeElement}
          <span className="rate">{outcomeRate}</span>
        </div>
        {/* success / fail / cancel */}
        {hitStateElement}
      </Fragment>
    )
  }

  if (small && vertical) {
    contents = (
      <div className="match-info">
        <div className="top-info">
          <span className="date">
            {`${date} ${time}`}
          </span>
          <div className="select-info">
            {pickStatus}
          </div>
        </div>

        <div className="score-wrapper">
          <div className="team home-team">
            <span className="emblem">
              <img src={homeTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]} alt="홈팀 엠블럼" loading="lazy"/>
            </span>
            {homeTeamName}
          </div>
          {isClosed && <span className={`score ${homeScoreClassName}`}>{homeTeamScore}</span>}
          {isCanceled && <span className="score">-</span>}
        </div>
        <div className="score-wrapper">
          <div className="team">
            <span className="emblem">
              <img src={awayTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]} alt="원정팀 엠블럼" loading="lazy"/>
            </span>
            {awayTeamName}
          </div>
          {isClosed && <span className={`score ${awayScoreClassName}`}>{awayTeamScore}</span>}
          {isCanceled && <span className="score">-</span>}
        </div>
      </div>
    )
  }

  return contents
})
